// packages/sdk/src/api/endpoint/base.ts
class ApiEndpoint {
  #client;
  constructor(client) {
    this.#client = client;
  }
  get client() {
    return this.#client;
  }
}

// packages/sdk/src/api/endpoint/agents.ts
class ApiAgents extends ApiEndpoint {
  async find(params) {
    const queryParams = new URLSearchParams;
    if (params?.per_page) {
      queryParams.append("per_page", params.per_page.toString());
    }
    if (params?.cursor) {
      queryParams.append("cursor", params.cursor);
    }
    const query = queryParams.toString();
    const path = query ? `athletes/agents?${query}` : "athletes/agents";
    return await this.client.get(path);
  }
  async invite(email) {
    return await this.client.post("athletes/agents", {
      email
    });
  }
  async delete(userId) {
    return await this.client.delete(`athletes/agents/${userId}`);
  }
}

// packages/sdk/src/api/endpoint/auth.ts
class ApiAuth extends ApiEndpoint {
  async login(payload, meta = {}) {
    meta.requiresUser = false;
    meta.requiresProfile = false;
    meta.requiresTou = false;
    const response = await this.client.post("auth/token", payload, meta);
    this.client.setInflcrToken(response.access_token);
    return response;
  }
  async logout(meta = {}) {
    meta.requiresProfile = false;
    meta.requiresTou = false;
    try {
      await this.client.delete("auth/token", undefined, meta);
    } finally {
      this.client.setInflcrToken(null);
      this.client.setInflcrUserId(null);
    }
  }
}

// packages/sdk/src/api/endpoint/media.ts
class ApiMedia extends ApiEndpoint {
  async attach(modelType, modelId, attachments) {
    return await this.client.post(`media/${modelType}/${modelId}`, {
      attachments
    });
  }
  async delete(mediaUuid) {
    return await this.client.delete(`media/${mediaUuid}`);
  }
  async deleteUpload(uploadId, key) {
    return await this.client.delete(`media/upload/s3/multipart/${uploadId}`, {
      key
    });
  }
}

// packages/sdk/src/api/endpoint/reviews.ts
class ApiReviews extends ApiEndpoint {
  async create(payload) {
    return await this.client.post("reviews", payload);
  }
  async update(uuid, payload) {
    return await this.client.patch(`reviews/${uuid}`, payload);
  }
  async delete(uuid) {
    const response = await this.client.delete(`reviews/${uuid}`);
    return response.status || "success";
  }
  async findMyReviews(params) {
    const queryParams = new URLSearchParams;
    if (params?.per_page) {
      queryParams.append("per_page", params.per_page.toString());
    }
    if (params?.cursor) {
      queryParams.append("cursor", params.cursor);
    }
    const query = queryParams.toString();
    const path = query ? `users/me/reviews?${query}` : "users/me/reviews";
    return await this.client.get(path);
  }
  async findProviderReviews(options) {
    const queryParams = new URLSearchParams;
    if (options.per_page) {
      queryParams.append("per_page", options.per_page.toString());
    }
    if (options.uuid) {
      queryParams.append("external_provider_uuid", options.uuid);
    }
    if (options.cursor) {
      queryParams.append("cursor", options.cursor);
    }
    if (options.term) {
      queryParams.append("term", options.term);
    }
    if (options.sort) {
      queryParams.append("sort[0][0]", options.sort.value);
      queryParams.append("sort[0][1]", options.sort.direction);
    }
    const query = queryParams.toString();
    const path = query ? `reviews?${query}` : "reviews";
    return await this.client.get(path);
  }
}

// packages/sdk/src/api/endpoint/transactions.ts
class ApiTransactions extends ApiEndpoint {
  async create(payload) {
    const response = await this.client.post("transactions", payload);
    return { status: response.status };
  }
  async update(payload) {
    const { uuid, ...body } = payload;
    return await this.client.patch(`transactions/${uuid}`, body);
  }
  async findOne(transactionId) {
    return await this.client.get(`transactions/${transactionId}`);
  }
  async findAll(options) {
    const queryParams = new URLSearchParams;
    queryParams.append("include_pre_submission_transactions", "true");
    if (options?.page !== undefined) {
      queryParams.append("page", options.page.toString());
    }
    if (options?.term) {
      queryParams.append("search", options.term);
    }
    if (options?.filters?.dueAt?.after) {
      queryParams.append("due_at[after]", options.filters.dueAt.after);
    }
    if (options?.filters?.dueAt?.before) {
      queryParams.append("due_at[before]", options.filters.dueAt.before);
    }
    if (options?.flagged) {
      queryParams.append("flagged", "true");
    }
    if (!options?.page || options.page <= 1) {
      queryParams.append("include_flagged_count", "true");
    }
    if (options?.leaderboard_status) {
      for (const status of options.leaderboard_status) {
        queryParams.append("leaderboard_status[]", status.toString());
      }
    }
    if (options?.sort) {
      queryParams.append("sort[0][0]", options.sort.value);
      queryParams.append("sort[0][1]", options.sort.direction);
    }
    const query = queryParams.toString();
    const path = `transactions?${query}`;
    return await this.client.get(path);
  }
  async updateStatus(transactionId, exchangeStatus) {
    return await this.client.patch(`transactions/${transactionId}/exchange-status`, {
      exchange_status: exchangeStatus
    });
  }
  async updateTask(transactionId, taskId, payload) {
    return await this.client.patch(`transactions/${transactionId}/tasks/${taskId}`, payload);
  }
}

// packages/sdk/src/api/endpoint/users.ts
class ApiUsers extends ApiEndpoint {
  async me(options, meta = {}) {
    meta.requiresProfile = false;
    meta.requiresTou = false;
    const version = options?.version ?? 1;
    if (version === 2) {
      const user2 = await this.client.get("v2/users/me", meta);
      return user2;
    }
    const user = await this.client.get("users/me", meta);
    return user;
  }
}

// packages/sdk/src/api/client.ts
var INFLCR_ENDPOINTS = {
  agents: ApiAgents,
  auth: ApiAuth,
  media: ApiMedia,
  reviews: ApiReviews,
  transactions: ApiTransactions,
  users: ApiUsers
};
var DEFAULT_REQUEST_META = {
  requiresUser: true,
  requiresProfile: true,
  requiresTou: true
};
var DEFAULT_HEADERS = {
  accept: "application/json",
  "content-type": "application/json",
  "inflcr-mobile-version": "0.0.0"
};
var DEFAULT_CLIENT = {
  serverUrl: "https://api.inflcr.io",
  production: false
};

class ApiClient {
  serverUrl;
  production;
  fetchClient = (url, options) => fetch(url, options);
  inflcrToken = null;
  inflcrUserId = null;
  endpoints;
  constructor(config) {
    this.serverUrl = config?.serverUrl || DEFAULT_CLIENT.serverUrl;
    this.production = config?.production || DEFAULT_CLIENT.production;
    this.fetchClient = config?.fetch || this.fetchClient;
    this.endpoints = Object.entries(INFLCR_ENDPOINTS).reduce((acc, [key, EndpointClass]) => {
      acc[key] = new EndpointClass(this);
      return acc;
    }, {});
    for (const key of Object.keys(INFLCR_ENDPOINTS)) {
      Object.defineProperty(this, key, {
        get() {
          return this.endpoints[key];
        },
        enumerable: true,
        configurable: false
      });
    }
  }
  buildUrl(path) {
    return `${this.serverUrl}/${path}`;
  }
  buildCurl(url, request) {
    const method = request.method || "GET";
    let curl = `curl -X ${method}`;
    if (request.headers) {
      const headers = request.headers;
      for (const [key, value] of Object.entries(headers)) {
        curl += ` -H "${key}: ${value}"`;
      }
    }
    if (request.body) {
      curl += ` -d '${request.body}'`;
    }
    curl += ` "${url}"`;
    return curl;
  }
  wrapCurl(curl) {
    return `

---------------
\uD83D\uDC40 curl command
---------------
${curl}
---------------
`;
  }
  buildRequest(method, body, meta) {
    meta = {
      ...DEFAULT_REQUEST_META,
      ...meta
    };
    const inflcrTokenHeader = this.inflcrToken ? { Authorization: `Bearer ${this.inflcrToken}` } : {};
    const inflcrUserIdHeader = this.inflcrUserId ? { "Inflcr-Id": this.inflcrUserId } : {};
    const request = {
      method,
      headers: {
        ...DEFAULT_HEADERS,
        ...meta.requiresUser ? inflcrTokenHeader : {},
        ...meta.requiresProfile ? inflcrUserIdHeader : {}
      },
      body: body ? JSON.stringify(body) : undefined
    };
    return [request, meta];
  }
  async handleResponse({
    url,
    request,
    response
  }) {
    const isError = !response.ok || response.status >= 400;
    let json = null;
    try {
      json = await response.json();
    } catch {}
    if (isError) {
      let message = json?.message ?? response.statusText;
      if (!this.production) {
        const curl = this.buildCurl(url, request);
        message += this.wrapCurl(curl);
        if (this.inflcrToken) {
          try {
            const decoded = this.decodeToken(this.inflcrToken);
            message += `

----------------
\uD83D\uDD11 Decoded Token
----------------
Header: ${JSON.stringify(decoded.header, null, 2)}
Payload: ${JSON.stringify(decoded.payload, null, 2)}
---------------
`;
          } catch (error) {
            message += `

----------------
❌ Token Debug
----------------
Token present but could not be decoded: ${error instanceof Error ? error.message : "Unknown error"}
---------------
`;
          }
        }
      }
      throw new Error(message);
    }
    return json;
  }
  async get(path, meta) {
    const url = this.buildUrl(path);
    const [request, newMeta] = this.buildRequest("GET", undefined, meta);
    const response = await this.fetchClient(url, request, newMeta);
    return await this.handleResponse({ url, request, response });
  }
  async post(path, body, meta) {
    const url = this.buildUrl(path);
    const [request, newMeta] = this.buildRequest("POST", body, meta);
    const response = await this.fetchClient(url, request, newMeta);
    return await this.handleResponse({ url, request, response });
  }
  async put(path, body, meta) {
    const url = this.buildUrl(path);
    const [request, newMeta] = this.buildRequest("PUT", body, meta);
    const response = await this.fetchClient(url, request, newMeta);
    return await this.handleResponse({ url, request, response });
  }
  async delete(path, body, meta) {
    const url = this.buildUrl(path);
    const [request, newMeta] = this.buildRequest("DELETE", body, meta);
    const response = await this.fetchClient(url, request, newMeta);
    return await this.handleResponse({ url, request, response });
  }
  async patch(path, body, meta) {
    const url = this.buildUrl(path);
    const [request, newMeta] = this.buildRequest("PATCH", body, meta);
    const response = await this.fetchClient(url, request, newMeta);
    return await this.handleResponse({ url, request, response });
  }
  getInflcrToken() {
    return this.inflcrToken;
  }
  setInflcrToken(token) {
    this.inflcrToken = token;
  }
  getInflcrUserId() {
    return this.inflcrUserId;
  }
  setInflcrUserId(userId) {
    this.inflcrUserId = userId;
  }
  getServerUrl() {
    return this.serverUrl;
  }
  setServerUrl(serverUrl) {
    this.serverUrl = serverUrl;
  }
  decodeToken(token) {
    const tokenToUse = token ?? this.inflcrToken;
    if (!tokenToUse || typeof tokenToUse !== "string") {
      throw new Error("Token must be a non-empty string. No token provided and no token is set internally.");
    }
    const parts = tokenToUse.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid token format. Expected 3 parts separated by dots.");
    }
    try {
      const [headerB64, payloadB64, signature] = tokenToUse.split(".");
      const decodeBase64Url = (str) => {
        let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
        const padding = base64.length % 4;
        if (padding) {
          base64 += "=".repeat(4 - padding);
        }
        if (typeof atob !== "undefined") {
          return atob(base64);
        } else if (typeof Buffer !== "undefined") {
          return Buffer.from(base64, "base64").toString("utf-8");
        } else {
          throw new Error("No base64 decoder available");
        }
      };
      const header = JSON.parse(decodeBase64Url(headerB64));
      const payload = JSON.parse(decodeBase64Url(payloadB64));
      return {
        header,
        payload,
        signature
      };
    } catch (error) {
      throw new Error(`Failed to decode token: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }
}
// package.json
var package_default = {
  name: "@teamworksdev/influencer",
  version: "3.0.0",
  type: "module",
  scripts: {
    typecheck: "tsc --noEmit",
    lint: "biome check",
    format: "biome check --write --unsafe",
    knip: "knip --no-config-hints",
    build: "bun format && bun lint --write && bun build:core:docs && bun ./scripts/build-packages",
    "build:core:docs": "bun run ./scripts/build-core-docs.ts",
    release: "semantic-release",
    "release:canary": "bash .gitlab/ci-publish-canary.sh",
    prepack: "cp -r ./dist/core ./ && cp -r ./dist/sdk ./ && cp -r ./dist/permissions ./",
    postpack: "rm -rf ./core ./sdk ./permissions",
    test: "vitest run",
    "test:watch": "vitest",
    "test:ui": "vitest --ui",
    "test:coverage": "vitest run --coverage --config=vitest.config.ts",
    coverage: "bun test:coverage && open coverage/index.html",
    codex: `OPENAI_API_KEY=$(grep '^OPENAI_API_KEY' .env | cut -d '=' -f2-) codex --config preferred_auth_method="apikey"`,
    migrate: "biome migrate --write"
  },
  exports: {
    "./core": {
      types: "./core/index.d.ts",
      default: "./core/index.js"
    },
    "./sdk": {
      types: "./sdk/index.d.ts",
      default: "./sdk/index.js"
    },
    "./permissions": {
      types: "./permissions/index.d.ts",
      default: "./permissions/index.js"
    }
  },
  files: [
    "core",
    "sdk",
    "permissions",
    "README.md",
    "CHANGELOG.md"
  ],
  workspaces: [
    "packages/*",
    "examples/*"
  ],
  devDependencies: {
    "@biomejs/biome": "^2.3.4",
    "@semantic-release/changelog": "^6.0.3",
    "@semantic-release/git": "^10.0.1",
    "@semantic-release/gitlab": "^13.2.9",
    "@types/bun": "latest",
    "@vitest/coverage-v8": "^4.0.8",
    "conventional-changelog-conventionalcommits": "^9.1.0",
    knip: "^5.68.0",
    "semantic-release": "^25.0.2",
    "vite-tsconfig-paths": "^5.1.4",
    vitest: "^4.0.8"
  },
  peerDependencies: {
    typescript: "^5"
  },
  dependencies: {}
};

// packages/sdk/src/influencer.ts
class Influencer {
  static version = package_default.version;
  #api;
  constructor(config) {
    this.#api = new ApiClient(config);
    console.debug(`Influencer SDK v${Influencer.version} initialized`);
  }
  get api() {
    return this.#api;
  }
}
export {
  Influencer,
  DEFAULT_REQUEST_META,
  DEFAULT_HEADERS,
  DEFAULT_CLIENT,
  ApiUsers,
  ApiTransactions,
  ApiReviews,
  ApiMedia,
  ApiEndpoint,
  ApiClient,
  ApiAuth,
  ApiAgents
};

//# debugId=3786A9078ABBDDB264756E2164756E21
